import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import {
  Button,
  Flex,
  Input,
  Spin,
  Text,
  Title,
  CheckIcon,
  CloseIcon,
  EditIcon,
  LoadingOutlined,
  type TextProps,
  type TextFamily,
} from '@/uicomponents';
import { Show } from '@/components/shared/show';
import {
  AUDIENCE_NAME_EMPTY_ERROR,
  AUDIENCE_NAME_MAX_LENGTH,
} from '@/app/audience-builder/lib/audience-name';
import styles from './editable-audience-name.module.css';

type DisplayAs = 'text' | 'title';
type IconTrigger = 'hover' | 'always';
type InputSize = 'small' | 'middle' | 'large';

const STYLES: Record<string, CSSProperties> = {
  displayWrap: { minWidth: 0, maxWidth: '100%', overflow: 'hidden' },
  editTriggerText: { flexShrink: 0, fontSize: 'var(--dz-font-size-lg)' },
  editTriggerTitle: { flexShrink: 0, fontSize: 'var(--dz-font-size-2xl)' },
  editRow: { flex: 1, minWidth: 0 },
  nameInput: { flex: 1, minWidth: 0, maxWidth: 'var(--dz-container-4xl)' },
  // Compact mode: full-width inside the card, no max cap. Italic
  // inheritance from the card's `.nameSlot` wrapper is reset so the
  // edit input renders in the canonical Inter regular style.
  nameInputCompact: { flex: 1, minWidth: 0, width: '100%', fontStyle: 'normal' },
};

export interface EditableAudienceNameHandle {
  enterEditMode: () => void;
}

export interface EditableAudienceNameProps {
  name: string;
  onSave: (next: string) => void | Promise<void>;
  isPending?: boolean;
  displayAs?: DisplayAs;
  textVariant?: TextProps['variant'];
  textWeight?: TextProps['weight'];
  textFamily?: TextFamily;
  textTone?: TextProps['tone'];
  /**
   * Heading semantics for the `displayAs='text'` path. `displayAs='title'` already renders a
   * real `Typography.Title`; this is the `Text` branch, which carries the page-title *scale*
   * with no heading role — so the audience overview and Request Confirmation had no
   * `h1`..`h6` at all (spec 12 · C4 / 19 · D2).
   */
  textHeadingLevel?: NonNullable<TextProps['headingLevel']>;
  titleLevel?: 1 | 2 | 3 | 4 | 5;
  inputSize?: InputSize;
  iconTrigger?: IconTrigger;
  hideEditIcon?: boolean;
  maxLength?: number;
  /** Inline error shown when the field is emptied. Defaults to the audience copy. */
  emptyError?: string;
  ariaLabel?: string;
  startEditingOnMount?: boolean;
  /**
   * Compact edit chrome: hides the explicit Save / Cancel buttons and the
   * character counter. Commit still fires on Enter / blur; cancel on Esc.
   * Used by the audience card's inline rename to match the design (no
   * visible action buttons).
   */
  compactEdit?: boolean;
  /** When true the name is purely display — no edit icon, no click-to-edit. */
  readOnly?: boolean;
}

export const EditableAudienceName = forwardRef<
  EditableAudienceNameHandle,
  EditableAudienceNameProps
>(
  (
    {
      name,
      onSave,
      isPending = false,
      displayAs = 'text',
      textVariant = 'body',
      textWeight = 'bold',
      textFamily,
      textTone,
      textHeadingLevel,
      titleLevel = 3,
      inputSize = 'middle',
      iconTrigger = 'hover',
      hideEditIcon = false,
      maxLength = AUDIENCE_NAME_MAX_LENGTH,
      emptyError = AUDIENCE_NAME_EMPTY_ERROR,
      ariaLabel,
      startEditingOnMount = false,
      compactEdit = false,
      readOnly = false,
    },
    ref,
  ) => {
    const [isEditing, setIsEditing] = useState(startEditingOnMount);
    const [editValue, setEditValue] = useState(name);
    const cancellingRef = useRef(false);
    const displayRef = useRef<HTMLElement | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        enterEditMode: () => {
          setEditValue(name);
          setIsEditing(true);
        },
      }),
      [name],
    );

    const trimmed = editValue.trim();
    const isEmpty = trimmed === '';
    const isUnchanged = trimmed === name;
    const saveDisabled = isEmpty || isUnchanged;

    const enterEditMode = useCallback(() => {
      setEditValue(name);
      setIsEditing(true);
    }, [name]);

    const exitEditMode = useCallback(() => {
      cancellingRef.current = true;
      setIsEditing(false);
      // Return focus to display element so keyboard users can re-edit.
      requestAnimationFrame(() => {
        displayRef.current?.focus();
        cancellingRef.current = false;
      });
    }, []);

    const commitSave = useCallback(() => {
      if (saveDisabled) return;
      onSave(trimmed);
      exitEditMode();
    }, [saveDisabled, onSave, trimmed, exitEditMode]);

    const cancel = useCallback(() => {
      setEditValue(name);
      exitEditMode();
    }, [name, exitEditMode]);

    const handleInputKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!saveDisabled) commitSave();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      },
      [saveDisabled, commitSave, cancel],
    );

    const handleInputBlur = useCallback(() => {
      if (cancellingRef.current) return;
      // Blur = submit. Mirrors Enter. If the value is empty or unchanged
      // (`saveDisabled`), we just exit without firing the API — no point
      // in a network round-trip that won't change anything, and an empty
      // name would 422 the rename endpoint anyway.
      if (saveDisabled) {
        cancel();
      } else {
        commitSave();
      }
    }, [saveDisabled, cancel, commitSave]);

    // Prevent the mousedown on confirm/cancel buttons from stealing focus
    // from the input, so our cancel-on-blur logic doesn't fire before the click.
    const suppressBlur = useCallback((e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
    }, []);

    // Clicks inside the edit chrome (input, confirm/cancel buttons) must not
    // bubble up to any clickable ancestor — e.g. the home card's
    // ``onClick={handleCardOpen}`` would otherwise navigate the user away
    // while they're typing.
    const stopBubble = useCallback((e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
    }, []);

    const iconClassName =
      iconTrigger === 'hover'
        ? `${styles.editTrigger} ${styles.editTriggerHover}`
        : styles.editTrigger;

    const isTitleMode = displayAs === 'title';
    const editButtonSize: 'large' | 'middle' = isTitleMode ? 'large' : 'middle';
    const editButtonStyle = isTitleMode ? STYLES.editTriggerTitle : STYLES.editTriggerText;

    const displayContent =
      displayAs === 'title' ? (
        <Title
          level={titleLevel}
          ref={(el) => {
            displayRef.current = el;
          }}
          ellipsis={{ tooltip: name }}
          className={styles.titleDisplay}
        >
          {name}
        </Title>
      ) : (
        <Text
          ref={(el) => {
            displayRef.current = el;
          }}
          variant={textVariant}
          weight={textWeight}
          family={textFamily}
          tone={textTone}
          headingLevel={textHeadingLevel}
          ellipsis={{ tooltip: name }}
        >
          {name}
        </Text>
      );

    const rootClassName = isTitleMode ? `${styles.root} ${styles.rootTitle}` : styles.root;
    const rootJustify = 'center';
    const showClickToEdit = hideEditIcon && !readOnly;
    const displayClickHandler = showClickToEdit ? enterEditMode : undefined;
    const displayClassName = showClickToEdit ? styles.clickableDisplay : undefined;
    const inputVariant: 'outlined' | 'borderless' = compactEdit ? 'outlined' : 'borderless';
    const inputStyle = compactEdit ? STYLES.nameInputCompact : STYLES.nameInput;
    // The default `.nameInput` class composes InlineEdit's
    // borderless-with-bottom-line style — that's exactly what we don't
    // want in compact mode (the card design uses the standard outlined
    // input chrome). Skip the class so antd's `outlined` variant wins.
    const inputClassName = compactEdit ? undefined : styles.nameInput;

    return (
      <Flex vertical justify={rootJustify} gap={4} className={rootClassName}>
        <Show when={!isEditing}>
          <Flex
            align='center'
            gap={8}
            style={STYLES.displayWrap}
            onClick={displayClickHandler}
            className={displayClassName}
          >
            {displayContent}
            <Show when={!hideEditIcon && !readOnly}>
              <Show
                when={!isPending}
                fallback={<Spin size='small' indicator={<LoadingOutlined spin />} />}
              >
                <Button
                  type='text'
                  size={editButtonSize}
                  icon={<EditIcon />}
                  // The caller's label ("Rename audience <name>") was reaching only the
                  // edit-mode input, so in read mode every trigger on a page announced the
                  // same bare "Rename".
                  aria-label={ariaLabel ?? 'Rename'}
                  className={iconClassName}
                  style={editButtonStyle}
                  onClick={enterEditMode}
                />
              </Show>
            </Show>
          </Flex>
        </Show>

        <Show when={isEditing}>
          <Flex align='center' gap={8} onClick={stopBubble} role='presentation'>
            {/* eslint-disable jsx-a11y/no-autofocus -- edit mode entered by user intent */}
            <Input
              size={inputSize}
              autoFocus
              maxLength={maxLength}
              value={editValue}
              aria-label={ariaLabel}
              className={inputClassName}
              style={inputStyle}
              variant={inputVariant}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onBlur={handleInputBlur}
            />
            {/* eslint-enable jsx-a11y/no-autofocus */}
            <Show when={!compactEdit}>
              <Button
                type='primary'
                size={inputSize}
                icon={<CheckIcon />}
                aria-label='Save name'
                disabled={saveDisabled}
                onMouseDown={suppressBlur}
                onClick={commitSave}
              />
              <Button
                size={inputSize}
                icon={<CloseIcon />}
                aria-label='Cancel'
                onMouseDown={suppressBlur}
                onClick={cancel}
              />
            </Show>
          </Flex>
          <Flex className={styles.errorSlot} aria-live='polite' onClick={stopBubble}>
            <Show when={isEmpty}>
              <Text tone='error' variant='caption' role='alert'>
                {emptyError}
              </Text>
            </Show>
          </Flex>
        </Show>
      </Flex>
    );
  },
);

EditableAudienceName.displayName = 'EditableAudienceName';
